import { CompanyType, type IntelCompaniesSortBy, type SortDirection } from '@@types/api.types';
import type { SortingState } from '@tanstack/react-table';
import { parseAsSortingState } from '@utils/sorting.util';
import { createParser, parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useMemo } from 'react';
import {
  DEFAULT_ORGANIZATIONS_PAGE_SIZE,
  DEFAULT_ORGANIZATIONS_SORTING,
  INTEL_ORGANIZATION_COMPANY_TYPES,
  deriveOrganizationSort,
} from '../intel-organizations-sort.util';

type IntelOrganizationsCatalogQueryParams = {
  limit: number;
  page: number;
  search?: string;
  sortBy?: IntelCompaniesSortBy;
  sortDirection?: SortDirection;
  type?: CompanyType;
};

type UseIntelOrganizationsCatalogQueryParamsReturn = {
  DEFAULT_SORTING: SortingState;
  handleLimitChange: (newLimit: number) => void;
  limit: number;
  page: number;
  queryParams: IntelOrganizationsCatalogQueryParams;
  search: string | null;
  setPage: (page: number) => Promise<URLSearchParams>;
  setSearch: (search: string | null) => Promise<URLSearchParams>;
  setSorting: (sorting: SortingState | null) => Promise<URLSearchParams>;
  setType: (type: CompanyType | null) => Promise<URLSearchParams>;
  sortingState: SortingState;
  type: CompanyType | null;
};

const isCompanyType = (value: string): value is CompanyType =>
  INTEL_ORGANIZATION_COMPANY_TYPES.some((companyType) => companyType === value);

const parseAsCompanyType = createParser({
  parse(queryValue: string): CompanyType | null {
    return isCompanyType(queryValue) ? queryValue : null;
  },
  serialize(value: CompanyType): string {
    return value;
  },
});

export const useIntelOrganizationsCatalogQueryParams = (): UseIntelOrganizationsCatalogQueryParamsReturn => {
  const [limit, setLimit] = useQueryState('limit', parseAsInteger.withDefault(DEFAULT_ORGANIZATIONS_PAGE_SIZE));
  const [page, setPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [search, setSearch] = useQueryState('search', parseAsString);
  const [sorting, setSorting] = useQueryState(
    'sorting',
    parseAsSortingState.withDefault(DEFAULT_ORGANIZATIONS_SORTING),
  );
  const [type, setType] = useQueryState('type', parseAsCompanyType);

  const sortingState: SortingState = sorting ?? DEFAULT_ORGANIZATIONS_SORTING;

  const queryParams = useMemo(() => {
    const { sortBy, sortDirection } = deriveOrganizationSort(sortingState);

    return {
      limit: limit ?? DEFAULT_ORGANIZATIONS_PAGE_SIZE,
      page: page ?? 1,
      search: search ?? undefined,
      sortBy,
      sortDirection,
      type: type ?? undefined,
    };
  }, [limit, page, search, sortingState, type]);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      void setLimit(newLimit);
      void setPage(1);
    },
    [setLimit, setPage],
  );

  return {
    DEFAULT_SORTING: DEFAULT_ORGANIZATIONS_SORTING,
    handleLimitChange,
    limit: limit ?? DEFAULT_ORGANIZATIONS_PAGE_SIZE,
    page: page ?? 1,
    queryParams,
    search,
    setPage,
    setSearch,
    setSorting,
    setType,
    sortingState,
    type,
  };
};
